import React, { useCallback } from 'react';
import { View } from 'react-native';
import {
  Banner,
  Button,
  Input,
  KeyboardScreenContainer,
  Select,
  Text,
} from '~/components';
import Footer from '../Footer';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { isWeb } from '~/utils/device';
import type { StepComponentProps } from '~/components/StepsForm';
import { isDateIntervalValid } from '~/utils/yup';
import useBagProtectionForm from '~/stores/bagProtectionForm';
import type { BagProtectionForm } from '~/@types/BagProtection/form';
import { useSalesFunnel } from '~/stores';

const schema = yup
  .object()
  .shape({
    birthDate: isDateIntervalValid(18, 70),
    gender: yup.string().required('Este campo é obrigatório'),
    maritalStatus: yup.string().required('Este campo é obrigatório'),
  })
  .required();

const BagProtectionContractPersonalInfo: React.FC<StepComponentProps> = ({
  onContinue,
}) => {
  const { setPersonalInfo } = useBagProtectionForm();
  const { setInfos } = useSalesFunnel();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<BagProtectionForm.PersonalInfo>({
    resolver: yupResolver(schema),
  });

  const onSubmit = useCallback(
    (data: BagProtectionForm.PersonalInfo) => {
      setPersonalInfo(data);
      setInfos({
        birthDate: data.birthDate,
        gender: data.gender!,
        maritalStatus: data.maritalStatus!,
      });
      onContinue?.();
    },
    [onContinue, setInfos, setPersonalInfo]
  );

  return (
    <KeyboardScreenContainer
      footer={
        <View className="flex xl:hidden">
          <Footer onSubmit={handleSubmit(onSubmit)} />
        </View>
      }
    >
      <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
        <Banner type="bag-protection" />

        <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24">
          <Text className="text-xl text-gray-600">Dados pessoais</Text>

          <Text className="mt-4 text-gray-400 text-sm mb-8">
            Agora nos informe os dados pessoais do segurado:
          </Text>

          <Input
            control={control}
            name="birthDate"
            label="Data de nascimento:"
            placeholder="Data de nascimento"
            keyboardType="number-pad"
            error={errors.birthDate?.message}
            maskConfig={{ type: 'custom', mask: '99/99/9999' }}
          />

          <Select
            control={control}
            name="gender"
            label="Selecione o gênero do segurado:"
            placeholder="Selecione o gênero"
            error={errors.gender?.message}
            items={[
              { label: 'Masculino', value: 'male' },
              { label: 'Feminino', value: 'female' },
              { label: 'Não informado', value: 'notInformed' },
            ]}
          />

          <Select
            control={control}
            name="maritalStatus"
            label="Estado civil do segurado:"
            placeholder="Selecione o estado civil"
            error={errors.maritalStatus?.message}
            items={[
              { label: 'Casado(a)', value: 'married' },
              { label: 'Solteiro(a)', value: 'single' },
              { label: 'Viúvo(a)', value: 'widower' },
              { label: 'Divorciado(a)', value: 'divorced' },
            ]}
          />

          {isWeb && (
            <View className="flex-1 justify-end hidden xl:flex">
              <Button className="w-full" onPress={handleSubmit(onSubmit)}>
                CONTINUAR
              </Button>
            </View>
          )}
        </View>
      </View>
    </KeyboardScreenContainer>
  );
};

export default BagProtectionContractPersonalInfo;
